import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";

import { apiService } from "src/services/api.service";
import { localStorageService } from "src/services/local-storage.service";
import { toast } from "src/components/Toast/ToastManager";
import { Modal, useModal } from "src/components/Modal/Modal";
import Confirm from "src/components/Confirm/Confirm";

import styles from "./MyClubsTab.module.scss";

interface IClubRow {
    club_id: string;
    club_name: string;
    location: string;
    logo_url?: string;
    cover_image_url?: string;
    is_favorite: boolean;
    is_opted_in: boolean;
}

const clubInitials = (name: string) => {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) {
        return "";
    }
    if (parts.length === 1) {
        return parts[0].slice(0, 2).toUpperCase();
    }
    return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
};

const MyClubsTab: React.FC = () => {
    const [clubs, setClubs] = useState<IClubRow[]>([]);
    const [loaded, setLoaded] = useState<boolean>(false);
    const [ready, setReady] = useState<boolean>(false);
    const [isUpdating, setIsUpdating] = useState<boolean>(false);
    const { toggle: toggleRemove, isShown: isRemoveShown } = useModal();
    const [clubToRemove, setClubToRemove] = useState<IClubRow | null>(null);

    const fetchClubs = useCallback(async () => {
        try {
            const response = await apiService.getUserClubs();
            setClubs(response.clubs);
        } catch (error) {
            toast.show({
                title: "My Clubs",
                content: "Unable to load clubs.",
                duration: 5000,
                type: "fail"
            });
        } finally {
            setReady(true);
        }
    }, []);

    useEffect(() => {
        if (!loaded) {
            setLoaded(true);
            fetchClubs();
        }
    }, [loaded, fetchClubs]);

    const handleFavoriteToggle = async (club: IClubRow) => {
        // If both will be unchecked after this toggle, show warning
        if (club.is_favorite && !club.is_opted_in) {
            setClubToRemove(club);
            toggleRemove();
            return;
        }

        setIsUpdating(true);
        try {
            const user = JSON.parse(localStorageService.getItem(localStorageService.USER) || "{}");
            let newFavorites = [...(user.favorite_clubs || [])];
            
            if (club.is_favorite) {
                // Remove from favorites
                newFavorites = newFavorites.filter(id => id !== club.club_id);
            } else {
                // Add to favorites
                newFavorites.push(club.club_id);
            }
            
            const updatedUser = await apiService.updateSelf({ favorite_clubs: newFavorites });
            localStorageService.setItem(localStorageService.USER, JSON.stringify(updatedUser));
            
            // Update local state
            setClubs(prev => prev.map(c => 
                c.club_id === club.club_id 
                    ? { ...c, is_favorite: !c.is_favorite }
                    : c
            ));
            
            toast.show({
                title: "My Clubs",
                content: club.is_favorite ? "Removed from favorites" : "Added to favorites",
                duration: 3000,
                type: "success"
            });
        } catch (error) {
            toast.show({
                title: "My Clubs",
                content: "Unable to update favorite status.",
                duration: 5000,
                type: "fail"
            });
        } finally {
            setIsUpdating(false);
        }
    };

    const handleOptInToggle = async (club: IClubRow) => {
        // If both will be unchecked after this toggle, show warning
        if (club.is_opted_in && !club.is_favorite) {
            setClubToRemove(club);
            toggleRemove();
            return;
        }

        setIsUpdating(true);
        try {
            if (club.is_opted_in) {
                await apiService.optOutOfClub(club.club_id);
            } else {
                await apiService.optInToClub(club.club_id);
            }
            
            // Update the user in localStorage
            const updatedUserResponse = await apiService.whoAmI();
            localStorageService.setItem(localStorageService.USER, JSON.stringify(updatedUserResponse));
            
            // Update local state
            setClubs(prev => prev.map(c => 
                c.club_id === club.club_id 
                    ? { ...c, is_opted_in: !c.is_opted_in }
                    : c
            ));
            
            toast.show({
                title: "My Clubs",
                content: club.is_opted_in 
                    ? `Opted out of communications from ${club.club_name}` 
                    : `Opted in to communications from ${club.club_name}`,
                duration: 3000,
                type: "success"
            });
        } catch (error) {
            toast.show({
                title: "My Clubs",
                content: "Unable to update opt-in status.",
                duration: 5000,
                type: "fail"
            });
        } finally {
            setIsUpdating(false);
        }
    };

    const confirmRemoveClub = async () => {
        if (!clubToRemove) return;

        setIsUpdating(true);
        try {
            // Get current user data
            const user = JSON.parse(localStorageService.getItem(localStorageService.USER) || "{}");
            
            // Remove from favorites if needed
            if (clubToRemove.is_favorite) {
                const newFavorites = (user.favorite_clubs || []).filter((id: string) => id !== clubToRemove.club_id);
                await apiService.updateSelf({ favorite_clubs: newFavorites });
            }
            
            // Remove from opt-in if needed
            if (clubToRemove.is_opted_in) {
                await apiService.optOutOfClub(clubToRemove.club_id);
            }
            
            // Update the user in localStorage
            const updatedUserResponse = await apiService.whoAmI();
            localStorageService.setItem(localStorageService.USER, JSON.stringify(updatedUserResponse));
            
            // Remove from local state
            setClubs(prev => prev.filter(c => c.club_id !== clubToRemove.club_id));
            
            toast.show({
                title: "My Clubs",
                content: `${clubToRemove.club_name} removed from your clubs`,
                duration: 3000,
                type: "success"
            });
            
            toggleRemove();
            setClubToRemove(null);
        } catch (error) {
            toast.show({
                title: "My Clubs",
                content: "Unable to remove club.",
                duration: 5000,
                type: "fail"
            });
        } finally {
            setIsUpdating(false);
        }
    };

    return (
        <div className={styles.MyClubsTab}>
            {ready && clubs.length === 0 ? (
                <div className={styles.emptyState}>
                    <p>You haven't added any clubs yet.</p>
                    <p>Browse clubs and click the heart icon to add them to your favorites.</p>
                </div>
            ) : (
                <div className={styles.grid}>
                    {clubs.map((club) => (
                        <article className={styles.card} key={club.club_id}>
                            <div className={styles.cover}>
                                {club.cover_image_url && (
                                    <img src={club.cover_image_url} alt="" />
                                )}
                            </div>
                            <div className={styles.body}>
                                <div className={styles.logo}>
                                    {club.logo_url ? (
                                        <img src={club.logo_url} alt="" />
                                    ) : (
                                        <span>{clubInitials(club.club_name)}</span>
                                    )}
                                </div>
                                <h3>{club.club_name}</h3>
                                {club.location && <p className={styles.location}>{club.location}</p>}
                                <div className={styles.prefs}>
                                    <button
                                        type="button"
                                        className={club.is_favorite ? styles.chipActive : styles.chip}
                                        aria-pressed={club.is_favorite}
                                        disabled={isUpdating}
                                        onClick={() => handleFavoriteToggle(club)}
                                    >
                                        <svg width="13" height="13" viewBox="0 0 24 24" fill={club.is_favorite ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                            <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1.1-1.1a5.5 5.5 0 0 0-7.8 7.8l1.1 1.1L12 21l7.8-7.5 1.1-1.1a5.5 5.5 0 0 0-.1-7.8Z" />
                                        </svg>
                                        Favorite
                                    </button>
                                    <button
                                        type="button"
                                        className={club.is_opted_in ? styles.chipActive : styles.chip}
                                        aria-pressed={club.is_opted_in}
                                        disabled={isUpdating}
                                        onClick={() => handleOptInToggle(club)}
                                    >
                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                            {club.is_opted_in ? <path d="m5 12 4 4L19 6" /> : <circle cx="12" cy="12" r="8" />}
                                        </svg>
                                        Opt In
                                    </button>
                                </div>
                                <Link className={styles.view} to={`/k/${club.club_id}`}>
                                    View club
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                        <path d="m9 18 6-6-6-6" />
                                    </svg>
                                </Link>
                            </div>
                        </article>
                    ))}
                </div>
            )}
            <Modal
                className="Confirmation"
                isShown={isRemoveShown}
                hide={toggleRemove}
                modalContent={
                    <Confirm
                        message={
                            <>
                                <p><strong>Remove Club?</strong></p>
                                <p>This will remove <strong>{clubToRemove?.club_name}</strong> from your list.</p>
                                <p>You will no longer receive updates or communications from them.</p>
                            </>
                        }
                        onConfirm={confirmRemoveClub}
                        onCancel={() => { toggleRemove(); setClubToRemove(null); }}
                        buttonsDisabled={isUpdating}
                    />
                }
            />
        </div>
    );
};

export default MyClubsTab;

